import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Copy } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  apiClient,
  type CreateTenantWithOwnerResponse,
} from '@/lib/api-client';

const formSchema = z.object({
  tenantName: z
    .string()
    .trim()
    .min(2, 'Informe o nome do escritório (mínimo 2 caracteres).')
    .max(120, 'Nome muito longo (máximo 120 caracteres).'),
  tenantSlug: z
    .string()
    .trim()
    .regex(
      /^[a-z0-9-]{3,50}$/,
      'Slug deve conter 3-50 caracteres, somente letras minúsculas, números e hifens.',
    ),
  ownerName: z
    .string()
    .trim()
    .min(2, 'Informe o nome do owner (mínimo 2 caracteres).')
    .max(120, 'Nome muito longo (máximo 120 caracteres).'),
  ownerEmail: z
    .string()
    .trim()
    .toLowerCase()
    .email('E-mail inválido.'),
});

type FormValues = z.infer<typeof formSchema>;

function extractApiMessage(err: unknown, fallback: string): string {
  const body = (err as { body?: unknown })?.body;
  const raw =
    (body as { message?: unknown })?.message ??
    (err as { message?: string })?.message;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object' && raw && 'message' in raw) {
    return String((raw as { message: unknown }).message);
  }
  return fallback;
}

export default function NovoClientePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateTenantWithOwnerResponse | null>(
    null,
  );
  const [copied, setCopied] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tenantName: '',
      tenantSlug: '',
      ownerName: '',
      ownerEmail: '',
    },
  });

  const createMutation = useMutation({
    mutationFn: (input: FormValues) =>
      apiClient.adminCreateTenantWithOwner(input),
    onSuccess: (data) => {
      setResult(data);
      // invalida lista para refletir novo tenant ao voltar
      void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
    onError: (err) => {
      setSubmitError(extractApiMessage(err, 'Falha ao cadastrar cliente.'));
    },
  });

  const onSubmit = handleSubmit((values) => {
    setSubmitError(null);
    createMutation.mutate(values);
  });

  async function handleCopy() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.firstAccessUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // navegador antigo / sem permissao — nada a fazer
    }
  }

  // Tela de sucesso
  if (result) {
    return (
      <div className="space-y-6">
        <header>
          <Link
            to="/admin/clientes"
            className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-neutral-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para a lista
          </Link>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Cliente criado com sucesso</CardTitle>
            <CardDescription>
              <strong>{result.tenant.name}</strong> ({result.tenant.slug}) foi
              cadastrado e <strong>{result.owner.email}</strong> está como
              owner pendente.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>URL de primeiro acesso</Label>
              <div className="flex items-stretch gap-2">
                <code className="flex-1 break-all rounded-md border border-neutral-800 bg-neutral-950 px-3 py-2 font-mono text-xs text-neutral-200">
                  {result.firstAccessUrl}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCopy}
                  className="shrink-0 gap-1.5"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Copiado
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copiar link
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-amber-400">
                Esta URL aparece apenas uma vez. Copie e envie ao owner pelo
                canal que preferir (whatsapp, e-mail manual, etc). Validade: 7
                dias.
              </p>
            </div>
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setResult(null);
                setSubmitError(null);
                reset();
              }}
            >
              Cadastrar outro
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/admin/clientes')}
            >
              Voltar para a lista
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Form
  return (
    <div className="space-y-6">
      <header>
        <Link
          to="/admin/clientes"
          className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-neutral-100"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para a lista
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight text-neutral-100">
          Novo cliente
        </h1>
        <p className="mt-1 text-sm text-neutral-400">
          Cria o escritório e o owner pendente. Você receberá uma URL para
          enviar ao owner ativar o acesso.
        </p>
      </header>

      <Card>
        <form onSubmit={onSubmit} noValidate>
          <CardHeader>
            <CardTitle>Dados do cadastro</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tenantName">Nome do escritório</Label>
              <Input
                id="tenantName"
                placeholder="Ex: Bianca Advocacia"
                aria-invalid={errors.tenantName ? 'true' : 'false'}
                {...register('tenantName')}
              />
              {errors.tenantName && (
                <p className="text-xs text-red-400">
                  {errors.tenantName.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="tenantSlug">Slug</Label>
              <Input
                id="tenantSlug"
                placeholder="ex: bianca-advocacia"
                aria-invalid={errors.tenantSlug ? 'true' : 'false'}
                {...register('tenantSlug', {
                  setValueAs: (v: string) =>
                    typeof v === 'string' ? v.toLowerCase().trim() : v,
                })}
              />
              <p className="text-xs text-neutral-500">
                Apenas letras minúsculas, números e hifens. 3-50 caracteres.
              </p>
              {errors.tenantSlug && (
                <p className="text-xs text-red-400">
                  {errors.tenantSlug.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="ownerName">Nome do owner</Label>
              <Input
                id="ownerName"
                placeholder="Ex: Bárbara Bianca"
                aria-invalid={errors.ownerName ? 'true' : 'false'}
                {...register('ownerName')}
              />
              {errors.ownerName && (
                <p className="text-xs text-red-400">
                  {errors.ownerName.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="ownerEmail">E-mail do owner</Label>
              <Input
                id="ownerEmail"
                type="email"
                placeholder="owner@escritorio.com.br"
                autoComplete="email"
                aria-invalid={errors.ownerEmail ? 'true' : 'false'}
                {...register('ownerEmail')}
              />
              {errors.ownerEmail && (
                <p className="text-xs text-red-400">
                  {errors.ownerEmail.message}
                </p>
              )}
            </div>
            {submitError && (
              <p className="text-sm text-red-400" role="alert">
                {submitError}
              </p>
            )}
          </CardContent>
          <CardFooter className="justify-end">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Cadastrando...' : 'Cadastrar cliente'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
